import { forwardRef } from 'react';
import { NumericFormatProps } from 'react-number-format';

import type {
  DefaultComponentProps,
  ElementSize,
  Nullable,
  OmitStrict,
  RenderPropFC,
} from '../../types';
import { cn } from '../../utils/styles';
import InputError from '../Input/components/InputError';
import InputLabel from '../Input/components/InputLabel';

import NumericInputBox from './components/NumericInputBox';
import { NumericInputClassNames } from './constants';
import { NumericInputRoot } from './NumericInput.styled';
import { NumericInputChangeHandler } from './types';

export type NumericInputProps = OmitStrict<
  NumericFormatProps,
  'getInputRef' | 'thousandSeparator' | 'onChange' | 'value'
> &
  DefaultComponentProps & {
    className?: string;
    defaultValue?: string | number;
    elementSize?: ElementSize;
    error?: string;
    unit?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    isErrorInitiallyHidden?: boolean;
    isErrorHidden?: boolean;
    isLabelHidden?: boolean;
    label?: string;
    tooltipLabel?: JSX.Element;
    value?: Nullable<string | number>;
    trailing?: RenderPropFC;
    leading?: RenderPropFC;
    onChange?: NumericInputChangeHandler;
    previewDecimalScale?: number;
  };

/**
 * @see https://s-yadav.github.io/react-number-format/docs/props
 */
const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  (props, inputRef) => {
    const {
      className,
      'data-testid': dataTestId,
      elementSize = 'large',
      error,
      isErrorInitiallyHidden: isErrorSpaceNotReserved,
      isErrorHidden,
      isLabelHidden,
      label,
      tooltipLabel,
      disabled,
      unit,
      trailing,
      leading,
      'aria-invalid': ariaInvalid,
      ...inputProps
    } = props;

    const hasError = !!error || (!!ariaInvalid && ariaInvalid !== 'false');

    return (
      <NumericInputRoot
        className={cn(NumericInputClassNames.Root, className)}
        data-testid={dataTestId}>
        <InputLabel
          hasError={hasError}
          isDisabled={disabled}
          isLabelHidden={isLabelHidden}
          label={label}
          tooltipLabel={tooltipLabel}
        />
        <NumericInputBox
          elementSize={elementSize}
          hasError={hasError}
          inputProps={{
            getInputRef: inputRef,
            disabled,
            'aria-label': label,
            ...inputProps,
          }}
          leading={leading}
          trailing={trailing}
          unit={unit}
        />
        <InputError
          elementSize={elementSize}
          error={error}
          inputTestId={dataTestId}
          isErrorHidden={isErrorHidden}
          isErrorSpaceReserved={!isErrorSpaceNotReserved}
        />
      </NumericInputRoot>
    );
  },
);

NumericInput.displayName = 'NumericInput';

export default NumericInput;
